import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
	LO_DIALOG_DATA,
	LoButton,
	LoDialog,
	LoDialogRef,
	LoNotice,
	LoSelect,
	LoSelectOption,
	LoSelectValue,
	LoTextField
} from 'lo-design-system';
import { Subject, takeUntil } from 'rxjs';
import { TeamService } from '@brand-lolly/app/user-management/team/services/team.service';
import { SeatRungOption } from '@brand-lolly/app/user-management/team/types/seat-rungs.util';
import { parseTeamRefusal } from '@brand-lolly/app/user-management/team/types/team-refusal.util';
import { TeamRefusalCode } from '@brand-lolly/app/user-management/team/types/team.types';
import {
	InviteMemberDialogData,
	InviteMemberDialogResult
} from './invite-member-dialog.model';

const EMAIL_PATTERN =
	/^[^\s@<>()[\]\\.,;:"]+(\.[^\s@<>()[\]\\.,;:"]+)*@([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}$/;

/**
 * Seating one person: their address, the allowance their seat carries, and what
 * that adds to the bill — said before it is committed, never after.
 *
 * THREE THINGS THIS DELIBERATELY DOES NOT ASK FOR.
 *
 * A TIER. The account holds one, every seat is licensed at it, and a seating on
 * any other tier is refused (`tier_mismatch`). A control whose every off-default
 * value is a refusal is not a choice.
 *
 * A ROLE. Not in this wave — the surface shows a member's role and does not
 * change it yet.
 *
 * A CONFIRMATION THAT THE ADDRESS IS FREE. The server knows and this dialog does
 * not, so it asks by sending: a 409 `email_already_registered` comes back to the
 * ADDRESS FIELD, where the mistake was made, rather than as a banner over a form
 * whose other half was fine.
 */
@Component({
	standalone: true,
	selector: 'app-invite-member-dialog',
	imports: [
		ReactiveFormsModule,
		LoDialog,
		LoButton,
		LoNotice,
		LoSelect,
		LoTextField
	],
	template: `
		<lo-dialog heading="Invite someone to your account" [closable]="!sending">
			<div class="fields">
				<p class="lo-type-text-md intro">
					They join on your {{ data.tierName }} plan — the tier is the
					account's, so the only thing to choose is how much of the
					monthly credit allowance their seat carries.
				</p>

				<lo-text-field
					type="email"
					label="Work email"
					placeholder="name@company.com"
					autocomplete="email"
					data-testid="invite-email"
					[formControl]="emailControl"
					[error]="emailError"
				/>

				<lo-select
					label="Monthly credit allowance for this seat"
					data-testid="invite-rung"
					[options]="rungOptions"
					[value]="selectedRungValue"
					[disabled]="sending || !rungOptions.length"
					[error]="rungFieldError"
					(valueChange)="onRungChange($event)"
				/>

				<section class="cost" data-testid="invite-cost">
					<div class="cost__row">
						<span class="lo-type-text-sm-medium cost__label">
							What this adds to your bill
						</span>
						<span
							class="lo-type-text-md-semibold"
							data-testid="invite-cost-now"
						>
							{{ costFigure }}
						</span>
					</div>
					<p class="lo-type-text-xs cost__note">{{ costNote }}</p>
				</section>

				@if (refusal) {
					<lo-notice compact
						tone="warning"
						data-testid="invite-refusal"
						[heading]="refusal"
					/>
				}
			</div>

			<div loDialogActions>
				<button
					lo-button
					variant="secondary"
					type="button"
					[disabled]="sending"
					(click)="close()"
				>
					Cancel
				</button>
				<button
					lo-button
					type="button"
					data-testid="invite-send"
					[loading]="sending"
					[disabled]="!canSend && !sending"
					(click)="send()"
				>
					Send invitation
				</button>
			</div>
		</lo-dialog>
	`,
	styles: [
		//language=scss
		`
			.fields {
				display: flex;
				flex-direction: column;
				gap: var(--lo-space-gap-lg);

				.intro,
				.cost__note {
					margin: 0;
					color: var(--lo-color-text-secondary);
				}

				.cost {
					display: flex;
					flex-direction: column;
					gap: var(--lo-space-gap-xs);

					.cost__row {
						display: flex;
						align-items: center;
						justify-content: space-between;
						gap: var(--lo-space-gap-lg);
					}

					.cost__label {
						color: var(--lo-color-text-secondary);
					}
				}
			}
		`
	]
})
export class InviteMemberDialogComponent implements OnInit, OnDestroy {
	readonly emailControl = new FormControl<string | null>('', [
		Validators.required,
		Validators.maxLength(120),
		Validators.pattern(EMAIL_PATTERN)
	]);

	rungOptions: LoSelectOption[] = [];
	selectedRungValue: string | null = null;

	/** The figure beside the cost label — the rung's own price, or an em dash. */
	costFigure = '—';
	/** The sentence under it, which says what that figure is and is not. */
	costNote = '';

	sending = false;
	/** The message under the address field — a refusal that belongs to it. */
	emailError = '';
	/** A refusal that belongs to the whole form rather than to one control. */
	refusal = '';
	/** Assigned, never a template getter — this is read on every pass. */
	canSend = false;
	/** The message under the allowance — a refusal that belongs to that control, or why the list is empty. */
	rungFieldError = '';

	private selectedPlanId: number | null = null;
	private readonly unsubscriber$ = new Subject<void>();

	readonly data = inject<InviteMemberDialogData>(LO_DIALOG_DATA);
	private readonly dialogRef =
		inject<
			LoDialogRef<InviteMemberDialogComponent, InviteMemberDialogResult>
		>(LoDialogRef);
	private readonly teamService = inject(TeamService);

	ngOnInit(): void {
		this.rungOptions = this.data.rungs.map(rung => ({
			label: rung.label,
			value: String(rung.planId)
		}));
		const first = this.data.rungs[0];
		if (first) {
			this.selectRung(String(first.planId));
		} else {
			this.rungFieldError =
				'We could not read the allowances on your plan. Reload the page and try again.';
			this.applyCost(null);
		}
		this.recomputeCanSend();

		this.emailControl.valueChanges
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => {
				// A refusal belongs to the value that earned it. The moment the
				// address changes it is no longer that value, so the messages
				// go. The control's own error clears itself: a value change
				// re-runs the validators, and that REPLACES whatever
				// setErrors put there.
				this.emailError = '';
				this.refusal = '';
				this.recomputeCanSend();
			});
	}

	ngOnDestroy(): void {
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	onRungChange(value: LoSelectValue): void {
		if (typeof value === 'string') {
			this.selectRung(value);
		}
	}

	selectRung(planId: string): void {
		this.selectedRungValue = planId;
		this.selectedPlanId = Number(planId);
		this.rungFieldError = '';
		this.refusal = '';
		this.applyCost(
			this.data.rungs.find(rung => rung.planId === this.selectedPlanId) ??
				null
		);
		this.recomputeCanSend();
	}

	send(): void {
		if (!this.canSend || this.selectedPlanId === null) {
			return;
		}
		const email = String(this.emailControl.value ?? '').trim();
		this.sending = true;
		this.refusal = '';
		this.emailError = '';
		this.rungFieldError = '';
		this.recomputeCanSend();

		this.teamService
			.createInvite({ email, rung_plan_id: this.selectedPlanId })
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe({
				next: response => {
					this.sending = false;
					this.dialogRef.close({
						invited: true,
						email,
						quote: response?.quote ?? null
					});
				},
				error: error => {
					this.sending = false;
					this.presentRefusal(error);
					this.recomputeCanSend();
				}
			});
	}

	close(): void {
		if (this.sending) {
			return;
		}
		this.dialogRef.close({ invited: false });
	}

	/**
	 * WHAT THE SEATING COSTS, stated before it is committed.
	 *
	 * The figure is the RUNG'S OWN CATALOGUE PRICE, not a proration, and the
	 * note beside it says so — `GET /team/seats/quote` is not routed and
	 * `POST /team/invites` answers `quote: null` until the billing wave lands,
	 * so there is no part-period amount to print. A catalogue price is a fact
	 * about the plan being bought; printing an invented proration instead, or
	 * printing nothing at all, are the two ways this could be wrong.
	 */
	private applyCost(rung: SeatRungOption | null): void {
		if (!rung) {
			this.costFigure = '—';
			this.costNote = 'Choose an allowance to see what the seat costs.';
			return;
		}
		if (!rung.priceLabel) {
			this.costFigure = 'Nothing';
			this.costNote =
				'This allowance costs nothing, so the seat adds nothing to your bill.';
			return;
		}
		this.costFigure = rung.priceLabel;
		this.costNote =
			'That is the seat from your next renewal. The part of this period that is left is added to your current invoice, on your own billing date.';
	}

	/**
	 * A refusal, put where the mistake was made.
	 *
	 * THE SERVER SAYS WHICH CONTROL. Two of the conflicts carry a `field` —
	 * `email` on both address clashes, `rung_plan_id` on a tier mismatch — so
	 * the placement is read off the response rather than hardcoded per code
	 * here; a refusal the server does not attribute to a control reads as a
	 * banner, which is correct for one that is about the account rather than
	 * about anything typed.
	 */
	private presentRefusal(error: unknown): void {
		const refusal = parseTeamRefusal(error);
		if (!refusal) {
			this.refusal =
				'The invitation could not be sent. Try again in a moment.';
			return;
		}
		const message = refusal.message ?? this.fallbackMessage(refusal.code);
		if (refusal.field === 'email') {
			this.emailError = message;
			this.emailControl.markAsTouched();
			this.emailControl.setErrors({
				...(this.emailControl.errors ?? {}),
				refused: true
			});
			return;
		}
		if (refusal.field === 'rung_plan_id') {
			this.rungFieldError = message;
			return;
		}
		this.refusal = message;
	}

	/** Our wording for a refusal the server sent without one. */
	private fallbackMessage(code: TeamRefusalCode): string {
		switch (code) {
			case TeamRefusalCode.LIMIT_EXCEEDED:
				return 'Every seat your plan admits is occupied. Freeze a seat, or move to a plan with more, before inviting anyone else.';
			case TeamRefusalCode.ROLE_FORBIDDEN:
				return 'Only the account owner can invite people to this account.';
			case TeamRefusalCode.SEAT_FROZEN:
				return 'Your own seat is frozen, so you cannot seat anyone else.';
			case TeamRefusalCode.FEATURE_DISABLED:
				return 'Your plan does not include team seats.';
			case TeamRefusalCode.NO_ACCOUNT_PLAN:
				return 'This account holds no plan, so there is no tier to seat anyone on.';
			default:
				return 'The invitation could not be sent. Try again in a moment.';
		}
	}

	/**
	 * The send control's own state.
	 *
	 * The cost is NOT a condition: the amount is stated above, but a rung whose
	 * price the catalogue could not describe must not become a wall between an
	 * owner and their own seating.
	 */
	private recomputeCanSend(): void {
		this.canSend =
			!this.sending &&
			this.emailControl.valid &&
			this.selectedPlanId !== null;
	}
}
